import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ADD_CLIENT,
  baseQuery,
  DELETE_CLIENT_BY_ID,
  GET_ALL_CLIENTS,
  UPDATE_CLIENT,
  UPDATE_CLIENT_STATUS,
} from "@/graphql";
import { setClients, setSelectedClient } from "./client-slice";
import { RootState } from "@/store";
import { signedUrlApi } from "../signed-url";

export const clientApi = createApi({
  reducerPath: "api/client",
  tagTypes: ["Clients"],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getAllClients: builder.query<
      SPOwnerClientResponse["getAllSpClients"],
      SPOwnerClientResponseVariables
    >({
      query: (variables) => ({
        document: GET_ALL_CLIENTS,
        variables: { ...variables },
      }),
      providesTags: ["Clients"],
      transformResponse: (response: SPOwnerClientResponse) => {
        return response.getAllSpClients;
      },
      onQueryStarted: async (_, { getState, dispatch, queryFulfilled }) => {
        const state = getState() as RootState;

        const { selectedClient } = state.client;
        const { data } = (await queryFulfilled).data;

        const clientsWithProfileImage = await Promise.all(
          data.map(async (client) => {
            if (client.profileImage) {
              const { signedUrl } = await dispatch(
                signedUrlApi.endpoints.getAccessSignedUrl.initiate({
                  fileName: client.profileImage,
                })
              ).unwrap();

              return { ...client, profileImage: signedUrl };
            }
            return client;
          })
        );

        const sortedClients = clientsWithProfileImage.sort(
          ({ firstName: nameA }, { firstName: nameB }) =>
            nameA.localeCompare(nameB)
        );

        dispatch(setClients(sortedClients));

        // Prefer persisted client selection (by ID) if available
        let hydrated = false;
        try {
          const storedClientId = localStorage.getItem("selectedClientId");
          if (storedClientId) {
            const match = sortedClients.find((c) => c._id === storedClientId);
            if (match && (!selectedClient || selectedClient._id !== storedClientId)) {
              dispatch(setSelectedClient(match));
              hydrated = true;
            }
          }
        } catch {}

        const updatedSelectedClient = sortedClients.find(
          (client) => client._id === selectedClient?._id
        );

        if (selectedClient && !updatedSelectedClient && sortedClients.length > 0) {
          dispatch(setSelectedClient(sortedClients[0]));
        } else if (
          sortedClients.length > 0 &&
          updatedSelectedClient &&
          selectedClient?.profileImage !== updatedSelectedClient?.profileImage
        ) {
          dispatch(setSelectedClient(updatedSelectedClient));
        } else if (!hydrated && !selectedClient && sortedClients.length > 0) {
          dispatch(setSelectedClient(sortedClients[0]));
        }
      },
    }),
    addClient: builder.mutation<boolean, { clientData: ClientType }>({
      query: ({ clientData }) => ({
        document: ADD_CLIENT,
        variables: { clientData },
      }),
      invalidatesTags: ["Clients"],
      transformResponse: (response) => response.createClient.data,
    }),
    updateClient: builder.mutation<
      void,
      { clientId: string; clientData: ClientType }
    >({
      query: ({ clientId, clientData }) => ({
        document: UPDATE_CLIENT,
        variables: { clientId, clientData },
      }),
      invalidatesTags: ["Clients"],
      transformResponse: (response) => response.updateClientInfo.data,
    }),
    deleteClient: builder.mutation<DeleteResponse, { userId: string ,tenantId?:string}>({
      query: (id) => ({
        document: DELETE_CLIENT_BY_ID,
        variables: id,
      }),
      invalidatesTags: ["Clients"],
    }),
    updateClientStatus: builder.mutation<
      IUpdateUserStatusResponse,
      IUpdateUserStatusRequest
    >({
      query: ({ email, isRestricted }) => ({
        document: UPDATE_CLIENT_STATUS,
        variables: {
          email,
          isRestricted,
        },
      }),
      invalidatesTags: ["Clients"],
    }),
  }),
});

export const {
  useGetAllClientsQuery,
  useAddClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useUpdateClientStatusMutation,
} = clientApi;
